import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// PageHeader v2 (web app v4 plan, Phase 3: one page kit). Every routed page
// opens with one: a mono eyebrow (the nav group, or a date), a light serif
// title that is the page's only <h1>, a one-line subtitle, an actions slot
// and, for a page with views, tabs. Three type sizes, all from the scale in
// theme/tokens.ts: xs (eyebrow), 2xl/3xl (title) and sm (subtitle, tabs).
//
// The tabs are links, because each view has its own address (it can be
// linked to, bookmarked and reached with Back), with tab semantics on top:
// a tablist, one selected tab in the tab order, arrow keys, Home and End to
// move between them, and Enter or Space to open one. The content below goes
// in a <PageTabPanel>, labelled by the selected tab.
// ---------------------------------------------------------------------------

export interface PageHeaderTab {
  /** Stable key; also makes the tab's id. */
  id: string;
  label: React.ReactNode;
  /** The view's address. */
  to: string;
  selected: boolean;
}

export interface PageHeaderTabs {
  /** What the tabs choose between, for screen readers ("Focus views"). */
  label: string;
  items: PageHeaderTab[];
  /** The id of the <PageTabPanel> the selected tab controls. */
  panelId: string;
}

export interface PageHeaderProps {
  /** Small mono line above the title: the nav group, or the date. */
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  /** One line under the title. */
  subtitle?: React.ReactNode;
  /** Buttons and controls, right-aligned beside the title (below it on a phone). */
  actions?: React.ReactNode;
  /** Views of this page, each its own address. Hidden when there's only one. */
  tabs?: PageHeaderTabs;
  /** Anything that belongs to the header under the subtitle (a status line, chips). */
  children?: React.ReactNode;
  className?: string;
  /** Id for the <h1>, when something else is labelled by it. */
  titleId?: string;
}

export const PAGE_TITLE_CLASS = 'font-serif text-2xl font-light leading-tight tracking-tight text-foreground md:text-3xl';
export const PAGE_EYEBROW_CLASS = 'font-plex text-xs uppercase tracking-[0.14em] text-muted-foreground';

export function pageTabId(panelId: string, tabId: string): string {
  return `${panelId}-tab-${tabId}`;
}

export function PageHeader({
  eyebrow, title, subtitle, actions, tabs, children, className, titleId,
}: PageHeaderProps) {
  const showTabs = !!tabs && tabs.items.length > 1;
  return (
    <header data-page-header="" className={cn(showTabs ? 'mb-5' : 'mb-6', className)}>
      {/* flex-wrap: on a phone the actions go under the title rather than squeeze it. */}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 max-w-full">
          {eyebrow && <p className={cn(PAGE_EYEBROW_CLASS, 'mb-1.5')}>{eyebrow}</p>}
          <h1 id={titleId} className={cn(PAGE_TITLE_CLASS, 'break-words')}>{title}</h1>
          {subtitle && <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>}
          {children}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {showTabs && <HeaderTabs {...tabs!} />}
    </header>
  );
}

// The tab just chosen from the keyboard. A view's own page draws the header
// (Focus time and Capacity are different pages), so choosing a tab can swap
// the whole header and drop focus to the page body. The new header puts it
// back on the same tab, if that happens straight away.
let chosenTab: { id: string; at: number } | null = null;

function rememberChosen(e: React.SyntheticEvent<HTMLAnchorElement>) {
  if (document.activeElement === e.currentTarget) chosenTab = { id: e.currentTarget.id, at: Date.now() };
}

function HeaderTabs({ label, items, panelId }: PageHeaderTabs) {
  const navigate = useNavigate();
  const refs = React.useRef<Array<HTMLAnchorElement | null>>([]);
  const selectedIndex = Math.max(0, items.findIndex(t => t.selected));

  React.useLayoutEffect(() => {
    const chosen = chosenTab;
    chosenTab = null;
    if (!chosen || Date.now() - chosen.at > 1000) return;
    const focused = document.activeElement;
    if (focused && focused !== document.body) return;
    refs.current.find(el => el?.id === chosen.id)?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>, index: number) => {
    // Alt+← (or ⌘+←) is the browser's Back; leave it, and every shortcut, alone.
    if (e.altKey || e.metaKey || e.ctrlKey) return;
    const last = items.length - 1;
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (e.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next !== null) {
      e.preventDefault();
      refs.current[next]?.focus();
      return;
    }
    // A link opens on Enter by itself; a tab opens on Space too.
    if (e.key === ' ') {
      e.preventDefault();
      rememberChosen(e);
      navigate(items[index].to);
    }
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      // Wraps rather than scrolls on a phone, so no tab is out of sight. The
      // rule under the row and the selected tab's bar are inset shadows, so
      // nothing hangs outside the row.
      className="mt-4 flex flex-wrap gap-x-1 shadow-[inset_0_-1px_0_hsl(var(--border))]"
    >
      {items.map((t, i) => (
        <Link
          key={t.id}
          ref={el => { refs.current[i] = el; }}
          to={t.to}
          role="tab"
          id={pageTabId(panelId, t.id)}
          aria-selected={t.selected}
          aria-controls={t.selected ? panelId : undefined}
          tabIndex={i === selectedIndex ? 0 : -1}
          onKeyDown={e => onKeyDown(e, i)}
          onClick={rememberChosen}
          className={cn(
            'inline-flex items-center gap-1.5 whitespace-nowrap rounded-t-md px-3 pb-2.5 pt-2 text-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
            // Selected: heavier text and a bar under it, not only a colour.
            t.selected
              ? 'font-semibold text-foreground shadow-[inset_0_-2px_0_hsl(var(--primary))]'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {t.label}
        </Link>
      ))}
    </div>
  );
}

/** The content a page's header tabs switch between: give it the same tabs. */
export function PageTabPanel({ tabs, className, children }: {
  tabs?: PageHeaderTabs;
  className?: string;
  children: React.ReactNode;
}) {
  const selected = tabs && tabs.items.length > 1 ? tabs.items.find(t => t.selected) : undefined;
  // Without tabs to label it (one view only) it's just the page's content.
  if (!tabs || !selected) return <div className={className}>{children}</div>;
  return (
    <div role="tabpanel" id={tabs.panelId} aria-labelledby={pageTabId(tabs.panelId, selected.id)} className={className}>
      {children}
    </div>
  );
}
